import castErrorToBaseError from '@/functions/castErrorToBaseError';
import config from '@/modules/config/functions/config';
import useAdminSettingsQuery from '@/modules/core/queries/useAdminSettingsQuery';
import DashboardWidgetWrapper from '@/modules/dashboard-settings/components/dashboard-widget-wrapper';
import SUPPORTED_WIDGETS from '@/modules/dashboard-settings/constants/supported-widgets';
import useUpdateDashboardConfigurationMutation from '@/modules/dashboard-settings/mutations/useUpdateDashboardConfigurationMutation';
import { DashboardSettingsSchema } from '@/modules/dashboard-settings/types/dashboard-settings';
import { PlusCircleOutlined } from '@ant-design/icons';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { IconSave } from '@thisisdmg/tirecloud-patterns';
import {
  BaseWidget,
  BREAKPOINT_2XL,
  BREAKPOINT_3XL,
  BREAKPOINT_LG,
  BREAKPOINT_MD,
  DashboardHeader,
  isEmpty,
  Skeleton,
  useNotification,
  WidgetGrid2,
  type SavedWidgetItem,
  type SupportedWidget,
} from '@thisisdmg/tirecloud-patterns/v2';
import { IconEdit } from '@thisisdmg/tirecloud-patterns/v2/components/icons';
import { type } from 'arktype';
import { cloneDeep, isEqual, isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './index.module.css';
import logger from '@/modules/logger/constants/logger';
import type { WidgetPreviewLanguage } from 'node_modules/@thisisdmg/tirecloud-patterns/dist/v2/components/widget/base';
import { match } from 'ts-pattern';

const PLACEHOLDER_WIDGET_ID = 'zzzz-placeholder';

function makeInstanceKey(widgetType: string): string {
  return `${widgetType}:${crypto.randomUUID()}`;
}

function getWidgetTypeFromKey(instanceKey: string): string {
  const colonIdx = instanceKey.indexOf(':');
  return colonIdx === -1 ? instanceKey : instanceKey.slice(0, colonIdx);
}

function DashboardPage() {
  const [editMode, setEditMode] = useState(false);
  const { t, i18n } = useTranslation();
  const updateDashboardConfigurationMutation =
    useUpdateDashboardConfigurationMutation();
  const notification = useNotification();

  type DashboardBreakpoint = 'sm' | 'md' | 'lg' | '2xl' | '3xl';

  const { data: adminSettingsData } = useAdminSettingsQuery();
  const [savedLayout, setSavedLayout] = useState<
    Partial<Record<DashboardBreakpoint, SavedWidgetItem[]>> | undefined
  >();
  const [widgetSettings, setWidgetSettings] = useState<Record<string, object>>(
    {}
  );
  useEffect(() => {
    if (
      isNil(adminSettingsData?.dashboardConfiguration) ||
      isEmpty(adminSettingsData.dashboardConfiguration)
    )
      return;
    if (savedLayout !== undefined) return;

    let dashboardSettingsObject: any;
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      dashboardSettingsObject = JSON.parse(
        adminSettingsData.dashboardConfiguration
      );
    } catch (error) {
      logger.error('dashboard-settings.load-configuration', error);
      notification.error({
        key: 'failed_to_load_dashboard_configuration',
        message: t(
          'dashboard_settings.feedback.failed_to_load_dashboard_configuration'
        ),
      });
      setSavedLayout({});
      return;
    }

    const dashboardSettings = DashboardSettingsSchema(dashboardSettingsObject);
    if (dashboardSettings instanceof type.errors) {
      notification.error({
        key: 'failed_to_load_dashboard_configuration',
        message: t(
          'dashboard_settings.feedback.failed_to_load_dashboard_configuration'
        ),
      });
      setSavedLayout({});
      return;
    }
    setSavedLayout(dashboardSettings.gridSettings);
    if (dashboardSettings.widgetSettings) {
      setWidgetSettings(dashboardSettings.widgetSettings);
    }
  }, [adminSettingsData, notification, savedLayout, t]);

  const instanceKeysInGrid = useMemo(() => {
    if (isNil(savedLayout) || isEmpty(savedLayout)) return [];
    const keys = new Set<string>();
    Object.values(savedLayout).forEach((layout) => {
      layout.forEach((item) => keys.add(item.key));
    });
    return Array.from(keys);
  }, [savedLayout]);

  const removeWidget = useCallback((instanceKey: string) => {
    setSavedLayout((prev) => {
      const newLayout = cloneDeep(prev ?? {});
      Object.keys(newLayout).forEach((breakpoint) => {
        newLayout[breakpoint as DashboardBreakpoint] = (
          newLayout[breakpoint as DashboardBreakpoint] ?? []
        ).filter((item) => item.key !== instanceKey);
      });
      return newLayout;
    });
    setWidgetSettings((prev) => {
      const next = { ...prev };
      delete next[instanceKey];
      return next;
    });
  }, []);

  const replaceWidget = useCallback(
    (instanceKey: string, newWidgetType: string) => {
      const newInstanceKey = makeInstanceKey(newWidgetType);
      setSavedLayout((prev) => {
        const newLayout = cloneDeep(prev ?? {});
        Object.keys(newLayout).forEach((breakpoint) => {
          newLayout[breakpoint as DashboardBreakpoint] = (
            newLayout[breakpoint as DashboardBreakpoint] ?? []
          ).map((item) =>
            item.key === instanceKey ? { ...item, key: newInstanceKey } : item
          );
        });
        return newLayout;
      });
      setWidgetSettings((prev) => {
        const next = { ...prev };
        delete next[instanceKey];
        return next;
      });
    },
    []
  );

  const updateWidgetSettings = useCallback(
    (widgetId: string, settings: object) => {
      setWidgetSettings((prev) => ({ ...prev, [widgetId]: settings }));
    },
    []
  );

  // Compute the bottom y position for a set of items (where the placeholder should sit)
  const getBottomY = useCallback((items: SavedWidgetItem[]) => {
    let maxBottom = 0;
    for (const item of items) {
      const bottom = (item.y ?? 0) + (item.h ?? 1);
      if (bottom > maxBottom) maxBottom = bottom;
    }
    return maxBottom;
  }, []);

  // savedLayout never contains the placeholder; displayLayout appends it at the bottom
  const displayLayout = useMemo(() => {
    if (!editMode) return savedLayout;
    if (isNil(savedLayout) || isEmpty(savedLayout)) return savedLayout;

    const result: Partial<Record<DashboardBreakpoint, SavedWidgetItem[]>> = {};
    for (const [bp, items] of Object.entries(savedLayout)) {
      const bottomY = getBottomY(items);
      result[bp as DashboardBreakpoint] = [
        ...items,
        { key: PLACEHOLDER_WIDGET_ID, x: 0, y: bottomY, w: 1, h: 2 },
      ];
    }
    return result;
  }, [editMode, getBottomY, savedLayout]);

  const supportedWidgets = useMemo(() => {
    return instanceKeysInGrid.reduce(
      (acc, instanceKey) => {
        const widgetType = getWidgetTypeFromKey(instanceKey);
        const widgetDef = SUPPORTED_WIDGETS.find((w) => w.id === widgetType);
        if (!widgetDef) return acc;

        acc[instanceKey] = {
          key: instanceKey,
          label: t(`dashboard_settings.builtin_widgets.${widgetDef.id}`),
          initialSize: widgetDef.prefer_size,
          content: ({ width, height }) => (
            <>
              <DashboardWidgetWrapper
                instanceKey={instanceKey}
                mode={editMode ? 'edit' : 'preview'}
                currentWidget={widgetDef}
                availableWidgets={SUPPORTED_WIDGETS.map((w) => w.id)}
                onClickReplaceWidget={(newWidgetType: string) => {
                  replaceWidget(instanceKey, newWidgetType);
                }}
                onClickRemoveWidget={() => {
                  removeWidget(instanceKey);
                }}
              >
                <widgetDef.widget
                  widgetId={instanceKey}
                  width={width}
                  height={height}
                  label={t(
                    `dashboard_settings.builtin_widgets.${widgetDef.id}`
                  )}
                  classNames={{
                    widgetInner: 'tc:pointer-events-none',
                  }}
                  preview={isNil(widgetSettings[instanceKey])}
                  previewLanguage={match<string, WidgetPreviewLanguage>(
                    i18n.language
                  )
                    .with('de', () => 'de')
                    .with('he', () => 'he')
                    .otherwise(() => 'en')}
                  {...(widgetSettings[instanceKey] as Record<string, unknown>)}
                />
              </DashboardWidgetWrapper>
              {'settings' in widgetDef && !isNil(widgetDef.settings) && (
                <widgetDef.settings
                  cardId={`${instanceKey}-widget-settings`}
                  value={widgetSettings[instanceKey]}
                  onChange={(data: unknown) =>
                    updateWidgetSettings(instanceKey, data as object)
                  }
                />
              )}
            </>
          ),
        };
        return acc;
      },
      {} as Record<string, SupportedWidget>
    );
  }, [
    editMode,
    i18n.language,
    instanceKeysInGrid,
    removeWidget,
    replaceWidget,
    t,
    updateWidgetSettings,
    widgetSettings,
  ]);

  const saveDashboard = useCallback(async () => {
    await updateDashboardConfigurationMutation
      .mutateAsync({
        version: 1,
        gridSettings: savedLayout ?? {},
        widgetSettings,
      })
      .then(() => {
        notification.success({
          message: t(
            'dashboard_settings.feedback.dashboard_configuration_saved'
          ),
        });
      })
      .catch((err) => {
        const e = castErrorToBaseError(err);
        notification.error({
          key: e.id,
          message: `0x${e.id.toString(16).toUpperCase()}`,
          description: t(`errors.gen.${e.path}`, { errorId: e.id }),
        });
      });
  }, [
    savedLayout,
    widgetSettings,
    updateDashboardConfigurationMutation,
    notification,
    t,
  ]);

  return (
    <>
      <DashboardHeader
        title={t('dashboard_settings.title')}
        className={classes.dashboard_header}
        ctaButtonProps={{
          onClick: () => {
            if (editMode) {
              saveDashboard();
            } else {
              setEditMode((prev) => !prev);
            }
          },
          children: editMode
            ? t('dashboard_settings.save_dashboard')
            : t('dashboard_settings.edit_dashboard'),
          icon: editMode ? <IconSave /> : <IconEdit />,
        }}
      />
      {savedLayout === undefined ? (
        <Skeleton active />
      ) : (
        <WidgetGrid2
          gridId="dashboard-settings"
          disabled={!editMode}
          supportedWidgets={{
            ...supportedWidgets,
            ...(editMode
              ? {
                  [PLACEHOLDER_WIDGET_ID]: {
                    key: PLACEHOLDER_WIDGET_ID,
                    label: 'Placeholder',
                    initialSize: 'm-tall',
                    content: ({ width, height }) => (
                      <DashboardWidgetWrapper
                        mode={editMode ? 'add' : 'preview'}
                        availableWidgets={SUPPORTED_WIDGETS.map(
                          (widget) => widget.id
                        )}
                        onClickAddWidget={(widgetId: string) => {
                          const instanceKey = makeInstanceKey(widgetId);
                          setSavedLayout((prev) => {
                            const newLayout = cloneDeep(prev ?? {});
                            const breakpointKeys =
                              Object.keys(newLayout).length > 0
                                ? Object.keys(newLayout)
                                : ([
                                    'sm',
                                    'md',
                                    'lg',
                                    '2xl',
                                    '3xl',
                                  ] as DashboardBreakpoint[]);
                            for (const bp of breakpointKeys) {
                              const items =
                                newLayout[bp as DashboardBreakpoint] ?? [];
                              const bottomY = getBottomY(items);
                              newLayout[bp as DashboardBreakpoint] = [
                                ...items,
                                {
                                  key: instanceKey,
                                  x: 0,
                                  y: bottomY,
                                  w: 1,
                                  h: 2,
                                } as SavedWidgetItem,
                              ];
                            }
                            return newLayout;
                          });
                        }}
                      >
                        <BaseWidget
                          widgetId={PLACEHOLDER_WIDGET_ID}
                          width={width}
                          height={height}
                          classNames={{
                            widgetInner: 'tc:h-full',
                            widgetContent: 'tc:h-full',
                            scrollArea: {
                              root: 'tc:h-full',
                            },
                          }}
                        >
                          <div className="tc:flex tc:items-center tc:justify-center tc:h-full tc:w-full">
                            <PlusCircleOutlined className="tc:text-[40px]" />
                          </div>
                        </BaseWidget>
                      </DashboardWidgetWrapper>
                    ),
                  },
                }
              : {}),
          }}
          containerPadding={[0, 0]}
          savedLayout={displayLayout}
          breakpoints={{
            sm: 0,
            md: BREAKPOINT_MD,
            lg: BREAKPOINT_LG,
            '2xl': BREAKPOINT_2XL,
            '3xl': BREAKPOINT_3XL,
          }}
          cols={{
            sm: 1,
            md: 2,
            lg: 2,
            '2xl': 3,
            '3xl': 4,
          }}
          onChange={(_currentLayout, allLayouts) => {
            // Strip the placeholder before persisting — it is always recomputed in displayLayout
            const withoutPlaceholder = Object.fromEntries(
              Object.entries(allLayouts).map(([bp, items]) => [
                bp,
                items.filter((item) => item.key !== PLACEHOLDER_WIDGET_ID),
              ])
            ) as Partial<Record<DashboardBreakpoint, SavedWidgetItem[]>>;
            if (!isEqual(withoutPlaceholder, savedLayout)) {
              setSavedLayout(withoutPlaceholder);
            }
          }}
        />
      )}
    </>
  );
}

export const Route = createFileRoute(
  '/_main-ui/_protected/dashboard-settings/'
)({
  component: DashboardPage,
  loader: async () => {
    if (config('features.dashboardSettings.enabled') !== true) throw notFound();
  },
});
